/**
 * Gmail Scan Service
 *
 * Microservice for scanning Gmail emails and extracting subscription information
 * Uses rule-based parsing to identify and extract subscription details
 */

import { supabase } from './supabaseClient';
import { getGmailAccessToken } from './gmailOAuthService';
import { classifyEmailsWithService, classifyEmailsPaidTier } from './classifierService';
import { getTierMode, getSelectedPaidModel } from './llmEmailFilterSecure';
import { convertLLMToSubscriptionFormat } from './llmEmailFilterSecure';
import { getUserSettings, getDateInTimezone } from './settingsService';
import { notifyPriceIncrease, notifyPriceDecrease, notifySyncCompleted, notifySyncFailed } from './notificationService';
import { recordSavingsEvent } from './savingsTrackerService';
// Groq SDK removed — website URL lookup now handled server-side via groq-website-lookup Edge Function

// ── Sync abort mechanism ─────────────────────────────────────────────
let _syncAborted = false;
let _syncAbortController = null;

class SyncAbortedError extends Error {
  constructor() { super('Sync aborted by user'); this.name = 'SyncAbortedError'; }
}

const checkAbort = () => {
  if (_syncAborted) throw new SyncAbortedError();
};

export const abortSync = () => {
  _syncAborted = true;
  if (_syncAbortController) {
    _syncAbortController.abort();
  }
  console.log('🛑 Sync abort requested — flag set + controller aborted');
};
// ─────────────────────────────────────────────────────────────────────

// Multi-pass search strategy: Organize keywords into targeted search passes
// This ensures comprehensive coverage without missing edge cases

// Pass 1: Receipt and Invoice keywords (strongest indicators)
const RECEIPT_KEYWORDS = [
  'receipt',
  'invoice',
  'payment confirmation',
  'payment received',
  'payment receipt'
];

// Pass 2: Billing and Transaction keywords
const BILLING_KEYWORDS = [
  'billing',
  'charged',
  'billed',
  'transaction',
  'order confirmation'
];

// Pass 3: Subscription and Renewal keywords
const SUBSCRIPTION_KEYWORDS = [
  'subscription',
  'renewal',
  'auto-renewal',
  'subscription renewed',
  'recurring payment',
  'your subscription'
];

// Pass 4: Periodic charge keywords
const PERIODIC_KEYWORDS = [
  'monthly charge',
  'annual charge',
  'quarterly charge',
  'payment successful',
  'yearly charge'
];

// Keywords to EXCLUDE promotional emails
const EXCLUDE_KEYWORDS = [
  'discount',
  'sale',
  'offer',
  'promotion',
  'limited time',
  'save',
  'deal',
  'coupon',
  'free trial',
  'upgrade now',
  'special offer',
  'get started',
  'try now'
];

// Common subscription service patterns
const SERVICE_PATTERNS = {
  // Streaming
  'netflix': { name: 'Netflix', category: 'Streaming', logo: '🎬' },
  'spotify': { name: 'Spotify', category: 'Music', logo: '🎵' },
  'hulu': { name: 'Hulu', category: 'Streaming', logo: '📱' },
  'disney': { name: 'Disney+', category: 'Streaming', logo: '🏰' },
  'youtube premium': { name: 'YouTube Premium', category: 'Streaming', logo: '📹' },
  'amazon prime': { name: 'Prime Video', category: 'Streaming', logo: '📺' },
  'hbo': { name: 'HBO Max', category: 'Streaming', logo: '🎭' },
  'paramount': { name: 'Paramount+', category: 'Streaming', logo: '⛰️' },
  'apple tv': { name: 'Apple TV+', category: 'Streaming', logo: '🍎' },

  // Software & Productivity
  'adobe': { name: 'Adobe Creative Cloud', category: 'Software', logo: '🎨' },
  'microsoft 365': { name: 'Microsoft 365', category: 'Productivity', logo: '📊' },
  'office 365': { name: 'Microsoft 365', category: 'Productivity', logo: '📊' },
  'notion': { name: 'Notion', category: 'Productivity', logo: '📝' },
  'evernote': { name: 'Evernote', category: 'Productivity', logo: '🐘' },
  'todoist': { name: 'Todoist', category: 'Productivity', logo: '✅' },
  'slack': { name: 'Slack', category: 'Communication', logo: '💬' },
  'zoom': { name: 'Zoom', category: 'Communication', logo: '📞' },
  'github': { name: 'GitHub', category: 'Development', logo: '💻' },
  'figma': { name: 'Figma', category: 'Design', logo: '🎯' },
  'canva': { name: 'Canva', category: 'Design', logo: '🎨' },

  // Cloud Storage
  'dropbox': { name: 'Dropbox', category: 'Cloud Storage', logo: '📦' },
  'google one': { name: 'Google One', category: 'Cloud Storage', logo: '☁️' },
  'icloud': { name: 'Apple iCloud+', category: 'Cloud Storage', logo: '☁️' },

  // Others
  'grammarly': { name: 'Grammarly', category: 'Productivity', logo: '✍️' },
  'chatgpt': { name: 'ChatGPT Plus', category: 'AI Tools', logo: '🤖' },
  'linkedin': { name: 'LinkedIn Premium', category: 'Professional', logo: '💼' },
  'audible': { name: 'Audible', category: 'Books', logo: '🎧' },
  'kindle': { name: 'Kindle Unlimited', category: 'Books', logo: '📚' },
  'nordvpn': { name: 'NordVPN', category: 'Security', logo: '🔒' },
  'lastpass': { name: 'LastPass', category: 'Security', logo: '🔐' },
  'headspace': { name: 'Headspace', category: 'Health & Fitness', logo: '🧘' },
  'peloton': { name: 'Peloton', category: 'Health & Fitness', logo: '🚴' },
  'discord': { name: 'Discord Nitro', category: 'Gaming', logo: '🎯' },
  'twitch': { name: 'Twitch Turbo', category: 'Gaming', logo: '🎮' }
};

/**
 * Build Gmail search queries using robust config-driven approach
 * @returns {Array<{name: string, query: string}>} Array of search passes
 */
const buildMultiPassQueries = () => {
  // Use config-driven query builder
  try {
    const { buildGmailSearchQueries } = require('./subscriptionEmailDetector.js');
    return buildGmailSearchQueries();
  } catch (error) {
    console.error('⚠️  Error loading config-driven queries:', error.message);

    // Fallback to simple queries
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const afterDate = twoYearsAgo.toISOString().split('T')[0].replace(/-/g, '/');

    console.log(`📅 Using fallback queries for emails after: ${afterDate}`);

    return [
      {
        name: 'Subscription Keywords (Fallback)',
        query: `(subscription OR membership) -in:sent -in:drafts after:${afterDate}`,
        priority: 1
      },
      {
        name: 'Invoice/Receipt (Fallback)',
        query: `(invoice OR receipt) -in:sent -in:drafts after:${afterDate}`,
        priority: 1
      }
    ];
  }
};

/**
 * Fetch message IDs for a single search query with pagination
 * @param {string} accessToken - Gmail API access token
 * @param {string} query - Gmail search query
 * @param {number} maxResults - Max results per page (max 500)
 * @returns {Promise<Array>} Array of message IDs
 */
const fetchMessageIdsForQuery = async (accessToken, query, maxResults = 500) => {
  let allMessageIds = [];
  let nextPageToken = null;
  let pageCount = 0;

  console.log(`   🔍 Query: "${query}"`);

  do {
    pageCount++;
    const url = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
    url.searchParams.set('q', query);
    url.searchParams.set('maxResults', maxResults.toString());
    if (nextPageToken) {
      url.searchParams.set('pageToken', nextPageToken);
    }

    console.log(`   📄 Fetching page ${pageCount}...`);

    const searchResponse = await fetch(url.toString(), {
      headers: { 'Authorization': `Bearer ${accessToken}` },
      signal: _syncAbortController?.signal
    });

    if (!searchResponse.ok) {
      const errorText = await searchResponse.text();
      console.error(`   ❌ Gmail API error (${searchResponse.status}):`, errorText);

      // Parse Google error for more details
      let errorDetails = '';
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorDetails = errorJson.error.message || errorJson.error;
          console.error(`   ❌ Google Error Message: ${errorDetails}`);
          console.error(`   ❌ Error Code: ${errorJson.error.code}`);
          console.error(`   ❌ Error Status: ${errorJson.error.status}`);
          if (errorJson.error.errors) {
            errorJson.error.errors.forEach(e => {
              console.error(`   ❌ Reason: ${e.reason}, Domain: ${e.domain}`);
            });
          }
        }
      } catch (e) {
        // errorText wasn't JSON
      }

      // Provide helpful guidance for 403 errors
      if (searchResponse.status === 403) {
        console.error('\n   🔧 403 FORBIDDEN - POSSIBLE CAUSES:');
        console.error('   1. Gmail API is NOT ENABLED in Google Cloud Console');
        console.error('      → Go to: https://console.cloud.google.com/apis/library/gmail.googleapis.com');
        console.error('      → Click "Enable"');
        console.error('   2. App is in "Testing" mode and user is not added as test user');
        console.error('      → Go to: https://console.cloud.google.com/apis/credentials/consent');
        console.error('      → Add user email under "Test users"');
        console.error('   3. OAuth consent screen scopes are not configured');
        console.error('      → Ensure gmail.readonly scope is added\n');
      }

      throw new Error(`Failed to search Gmail messages: ${searchResponse.status}${errorDetails ? ` - ${errorDetails}` : ''}`);
    }

    const searchData = await searchResponse.json();
    console.log(`   📊 API Response:`, {
      hasMessages: !!searchData.messages,
      messageCount: searchData.messages?.length || 0,
      hasNextPage: !!searchData.nextPageToken,
      resultSizeEstimate: searchData.resultSizeEstimate
    });

    // Only warn for very low counts that might indicate a real issue
    if (searchData.resultSizeEstimate && searchData.resultSizeEstimate < 10) {
      console.warn(`   ⚠️  Low result count: only ${searchData.resultSizeEstimate} emails matched this query`);
    }

    if (searchData.messages && searchData.messages.length > 0) {
      allMessageIds.push(...searchData.messages);
      console.log(`   ✅ Page ${pageCount}: ${searchData.messages.length} emails (total so far: ${allMessageIds.length})`);
    } else {
      console.log(`   ⚠️  Page ${pageCount}: No messages found`);
    }

    nextPageToken = searchData.nextPageToken;
  } while (nextPageToken);

  console.log(`   ✅ Total emails found for this query: ${allMessageIds.length}\n`);
  return allMessageIds;
};

/**
 * Fetch subscription emails from Gmail using multi-pass search strategy
 * Runs multiple targeted searches and deduplicates results
 * @param {string} accessToken - Gmail API access token
 * @returns {Promise<Array>} List of email messages
 */
const fetchSubscriptionEmails = async (accessToken) => {
  try {
    console.log('🔍 Starting multi-pass Gmail search...');

    const searchPasses = buildMultiPassQueries();
    const passResults = {};
    let allMessageIds = [];

    // Run all search passes
    for (const pass of searchPasses) {
      console.log(`\n🔎 Pass: ${pass.name}`);
      const messageIds = await fetchMessageIdsForQuery(accessToken, pass.query);
      passResults[pass.name] = messageIds.length;
      console.log(`   Found ${messageIds.length} emails`);
      allMessageIds.push(...messageIds);
    }

    console.log(`\n📊 Search Pass Summary:`);
    Object.entries(passResults).forEach(([name, count]) => {
      console.log(`   ${name}: ${count} emails`);
    });

    // Deduplicate by message ID
    const uniqueMessageIds = [...new Map(allMessageIds.map(msg => [msg.id, msg])).values()];
    const duplicatesRemoved = allMessageIds.length - uniqueMessageIds.length;

    console.log(`\n📧 Total emails before deduplication: ${allMessageIds.length}`);
    console.log(`🗑️  Duplicates removed: ${duplicatesRemoved}`);
    console.log(`✅ Unique emails to process: ${uniqueMessageIds.length}`);

    if (uniqueMessageIds.length === 0) {
      return [];
    }

    // ── Google Batch API with rate-limit handling ───────────────────────
    // Per Google docs: "sending batches with a large number of requests
    // can trigger [concurrent request limit]". Use smaller batches (20)
    // and retry failed items with exponential backoff (min 1s).
    // Ref: https://developers.google.com/gmail/api/guides/batch
    // Ref: https://developers.google.com/workspace/gmail/api/guides/handle-errors
    const BATCH_SIZE = 20;  // Google recommends <=50, but concurrent limit needs smaller
    const messages = [];
    const totalBatches = Math.ceil(uniqueMessageIds.length / BATCH_SIZE);

    console.log(`\n📥 Fetching message details in ${totalBatches} batches (Batch API, ${BATCH_SIZE}/batch)...`);

    /**
     * Send a batch of messages.get requests via Google's Batch API.
     * Returns { succeeded: [...msgObjects], failedIds: [...ids] }
     */
    const fetchMessagesBatch = async (messageIds) => {
      const boundary = `batch_subby_${Date.now()}`;

      // Build multipart/mixed request body
      let body = '';
      for (let idx = 0; idx < messageIds.length; idx++) {
        body += `--${boundary}\r\n`;
        body += `Content-Type: application/http\r\n`;
        body += `Content-ID: <msg-${messageIds[idx]}>\r\n\r\n`;
        body += `GET /gmail/v1/users/me/messages/${messageIds[idx]}?format=full\r\n\r\n`;
      }
      body += `--${boundary}--\r\n`;

      const response = await fetch('https://www.googleapis.com/batch/gmail/v1', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': `multipart/mixed; boundary=${boundary}`
        },
        body,
        signal: _syncAbortController?.signal
      });

      // If the entire batch request fails
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error(`❌ Batch API error (HTTP ${response.status}):`, errText);
        return { succeeded: [], failedIds: [...messageIds] };
      }

      // Parse multipart/mixed response
      const contentType = response.headers.get('Content-Type') || '';
      const respBoundaryMatch = contentType.match(/boundary=(.+)/);
      if (!respBoundaryMatch) {
        console.error('❌ Could not parse batch response boundary');
        return { succeeded: [], failedIds: [...messageIds] };
      }
      const respBoundary = respBoundaryMatch[1].trim();
      const responseText = await response.text();
      const parts = responseText.split(`--${respBoundary}`);

      const succeeded = [];
      const failedIds = [];
      // Track which IDs we've seen a successful response for
      const succeededIdSet = new Set();

      for (const part of parts) {
        if (!part.trim() || part.trim() === '--') continue;

        // Extract the Content-ID to map back to the message ID
        const contentIdMatch = part.match(/Content-ID:\s*<response-msg-([^>]+)>/i);

        // Find the JSON body
        const jsonStart = part.indexOf('\n{');
        if (jsonStart === -1) continue;

        const jsonStr = part.substring(jsonStart + 1).trim();
        const jsonEnd = jsonStr.lastIndexOf('}');
        if (jsonEnd === -1) continue;

        try {
          const parsed = JSON.parse(jsonStr.substring(0, jsonEnd + 1));
          if (parsed.id) {
            succeeded.push(parsed);
            succeededIdSet.add(parsed.id);
          } else if (parsed.error) {
            // This individual item failed (e.g. 429 within the batch)
            if (contentIdMatch) {
              failedIds.push(contentIdMatch[1]);
            }
          }
        } catch {
          // Parse error — count as failure
          if (contentIdMatch) {
            failedIds.push(contentIdMatch[1]);
          }
        }
      }

      // Any IDs not in succeeded set are failures (catches edge cases)
      for (const id of messageIds) {
        if (!succeededIdSet.has(id) && !failedIds.includes(id)) {
          failedIds.push(id);
        }
      }

      return { succeeded, failedIds };
    };

    // Process all messages in batches with retry for failed items
    for (let i = 0; i < uniqueMessageIds.length; i += BATCH_SIZE) {
      checkAbort();
      const batch = uniqueMessageIds.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const batchIds = batch.map(msg => msg.id);

      console.log(`   Batch ${batchNum}/${totalBatches} (${batch.length} messages)...`);

      // First attempt
      let result = await fetchMessagesBatch(batchIds);
      messages.push(...result.succeeded);

      // Retry failed items with exponential backoff (per Google: start at >=1s)
      let retryIds = result.failedIds;
      const MAX_RETRIES = 3;
      for (let attempt = 1; attempt <= MAX_RETRIES && retryIds.length > 0; attempt++) {
        const backoffMs = Math.pow(2, attempt - 1) * 1000 + Math.random() * 500;
        console.warn(`   ⏳ ${retryIds.length} items failed, retrying in ${Math.round(backoffMs)}ms (attempt ${attempt}/${MAX_RETRIES})...`);
        await new Promise(r => setTimeout(r, backoffMs));

        checkAbort();
        const retryResult = await fetchMessagesBatch(retryIds);
        messages.push(...retryResult.succeeded);
        retryIds = retryResult.failedIds;
      }

      if (retryIds.length > 0) {
        console.warn(`   ⚠️  ${retryIds.length} message(s) could not be fetched after ${MAX_RETRIES} retries`);
      }

      // Small delay between batches to avoid hitting concurrent limit
      if (i + BATCH_SIZE < uniqueMessageIds.length) {
        await new Promise(r => setTimeout(r, 300));
      }
    }
    // ───────────────────────────────────────────────────────────────────

    console.log(`✅ Successfully fetched ${messages.length} full messages\n`);

    // DEBUG: Check if OpenAI/ChatGPT email is in the results
    console.log(`\n🔍 CHECKING FOR OPENAI/CHATGPT EMAILS...`);
    const openaiEmails = messages.filter(msg => {
      const subject = msg.payload.headers.find(h => h.name.toLowerCase() === 'subject')?.value || '';
      const from = msg.payload.headers.find(h => h.name.toLowerCase() === 'from')?.value || '';
      const searchText = `${subject} ${from}`.toLowerCase();
      return searchText.includes('openai') || searchText.includes('chatgpt');
    });

    if (openaiEmails.length > 0) {
      console.log(`✅ FOUND ${openaiEmails.length} OpenAI/ChatGPT email(s)!`);
      openaiEmails.forEach((msg, idx) => {
        const subject = msg.payload.headers.find(h => h.name.toLowerCase() === 'subject')?.value || '';
        const from = msg.payload.headers.find(h => h.name.toLowerCase() === 'from')?.value || '';
        const date = msg.payload.headers.find(h => h.name.toLowerCase() === 'date')?.value || '';
        console.log(`   ${idx + 1}. Subject: "${subject}"`);
        console.log(`      From: ${from}`);
        console.log(`      Date: ${date}`);
      });
    } else {
      console.log(`❌ NO OpenAI/ChatGPT emails found in the ${messages.length} fetched emails`);
      console.log(`   This means the Gmail search queries didn't match the OpenAI email.`);
    }
    console.log(``);

    return messages;
  } catch (error) {
    console.error('Error fetching subscription emails:', error);
    throw error;
  }
};

/**
 * Extract header value from email
 * @param {Object} message - Gmail message object
 * @param {string} headerName - Header name to extract
 * @returns {string|null} Header value
 */
const getHeader = (message, headerName) => {
  const header = message.payload.headers.find(
    h => h.name.toLowerCase() === headerName.toLowerCase()
  );
  return header ? header.value : null;
};

/**
 * Decode base64url encoded string
 * @param {string} str - Base64url encoded string
 * @returns {string} Decoded string
 */
const decodeBase64Url = (str) => {
  try {
    // Convert base64url to base64
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // Pad with = if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    return atob(base64);
  } catch (error) {
    console.error('Error decoding base64:', error);
    return '';
  }
};

/**
 * Get email body text from message
 * @param {Object} message - Gmail message object
 * @returns {string} Email body text
 */
const getEmailBody = (message) => {
  let body = '';

  const getPart = (part) => {
    if (part.mimeType === 'text/plain' && part.body.data) {
      body += decodeBase64Url(part.body.data) + '\n';
    } else if (part.mimeType === 'text/html' && part.body.data && !body) {
      // Use HTML if no plain text available
      const html = decodeBase64Url(part.body.data);
      // Simple HTML to text conversion
      body += html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ') + '\n';
    }

    if (part.parts) {
      part.parts.forEach(getPart);
    }
  };

  if (message.payload.parts) {
    message.payload.parts.forEach(getPart);
  } else if (message.payload.body.data) {
    body = decodeBase64Url(message.payload.body.data);
  }

  return body;
};

/**
 * Get HTML body from message (preserves HTML for rendering)
 * @param {Object} message - Gmail message object
 * @returns {string} Email HTML body
 */
const getEmailHtmlBody = (message) => {
  let htmlBody = '';

  const getPart = (part) => {
    // Prioritize HTML content
    if (part.mimeType === 'text/html' && part.body.data) {
      const html = decodeBase64Url(part.body.data);
      if (!htmlBody || html.length > htmlBody.length) {
        htmlBody = html;
      }
    }

    if (part.parts) {
      part.parts.forEach(getPart);
    }
  };

  if (message.payload.parts) {
    message.payload.parts.forEach(getPart);
  } else if (message.payload.body.data) {
    // Check if the main body is HTML
    if (message.payload.mimeType === 'text/html') {
      htmlBody = decodeBase64Url(message.payload.body.data);
    }
  }

  return htmlBody;
};

/**
 * Extract URLs from email body HTML
 * @param {string} htmlBody - Email HTML body
 * @param {string} serviceName - Service name to match against
 * @returns {string|null} Best matching URL or null
 */
const extractUrlFromEmailBody = (htmlBody, serviceName) => {
  if (!htmlBody) return null;

  // Extract all URLs from href attributes
  const urlRegex = /href=["']([^"']+)["']/gi;
  const urls = [];
  let match;

  while ((match = urlRegex.exec(htmlBody)) !== null) {
    const url = match[1];
    // Filter out common non-website URLs
    if (url &&
      !url.startsWith('mailto:') &&
      !url.startsWith('#') &&
      !url.includes('unsubscribe') &&
      !url.includes('email-preferences') &&
      !url.includes('tracking') &&
      (url.startsWith('http://') || url.startsWith('https://'))) {
      urls.push(url);
    }
  }

  if (urls.length === 0) return null;

  // Try to find URL that matches service name
  const serviceNameLower = serviceName.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const url of urls) {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.toLowerCase().replace('www.', '');
      if (domain.includes(serviceNameLower) || serviceNameLower.includes(domain.split('.')[0])) {
        // Return clean base URL
        return `${urlObj.protocol}//${urlObj.hostname}`;
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }

  // If no match, return the first valid URL that looks like a main domain
  for (const url of urls) {
    try {
      const urlObj = new URL(url);
      // Prefer URLs that are just the base domain (no long paths)
      if (urlObj.pathname === '/' || urlObj.pathname === '') {
        return `${urlObj.protocol}//${urlObj.hostname}`;
      }
    } catch (e) {
      // Invalid URL, skip
    }
  }

  return null;
};

/**
 * Extract base domain from URL or email address
 * Handles both full URLs and email addresses, returns base domain (e.g., "canva.com")
 * Strips subdomains like "mail.", "www.", "noreply.", etc.
 * @param {string} input - URL or email address
 * @returns {string|null} Base domain or null
 */
const extractBaseDomain = (input) => {
  if (!input) return null;

  try {
    let hostname = '';

    // If it's a URL, extract hostname
    if (input.startsWith('http://') || input.startsWith('https://')) {
      const urlObj = new URL(input);
      hostname = urlObj.hostname.toLowerCase();
    }
    // If it's an email address, extract domain
    else if (input.includes('@')) {
      const emailMatch = input.match(/([a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,})/i);
      if (emailMatch) {
        hostname = emailMatch[1].split('@')[1].toLowerCase();
      } else {
        return null;
      }
    }
    // If it's just a domain, use as-is
    else if (input.includes('.')) {
      hostname = input.toLowerCase();
    } else {
      return null;
    }

    // Strip common subdomains (www, mail, noreply, etc.)
    hostname = hostname.replace(/^(www|mail|noreply|no-reply|smtp|webmail|email|notifications|notify|alerts|info|newsletter|news|updates|support|help|api|app|secure|login|account)\./i, '');

    // Extract the base domain (last two parts: domain.tld or domain.co.uk)
    const parts = hostname.split('.');

    // Handle common two-part TLDs (co.uk, com.au, etc.)
    const twoPartTLDs = ['co.uk', 'com.au', 'co.nz', 'com.br', 'co.za', 'com.mx', 'co.in', 'co.jp'];
    const lastTwo = parts.slice(-2).join('.');

    if (twoPartTLDs.includes(lastTwo) && parts.length >= 3) {
      // Return last 3 parts for two-part TLDs
      return parts.slice(-3).join('.');
    } else if (parts.length >= 2) {
      // Return last 2 parts for regular TLDs
      return parts.slice(-2).join('.');
    }

    return hostname;
  } catch (e) {
    console.error(`      ⚠️  Error extracting base domain from "${input}":`, e.message);
    return null;
  }
};

/**
 * Get website URL using Groq LLM fallback
 * @param {string} serviceName - Service name
 * @returns {Promise<string|null>} Website URL or null
 */
const getWebsiteUrlFromGroq = async (serviceName) => {
  try {
    console.log(`      🤖 Using Groq LLM (server-side) to find website for: ${serviceName}`);

    const { data, error } = await supabase.functions.invoke('groq-website-lookup', {
      body: { serviceName }
    });

    if (error) {
      console.error(`      ❌ Edge Function error:`, error.message);
      return null;
    }

    if (data?.url) {
      console.log(`      ✅ Groq found website: ${data.url}`);
      return data.url;
    }

    console.log(`      ⚠️  No valid URL found: ${data?.reason || 'unknown'}`);
    return null;
  } catch (error) {
    console.error(`      ❌ Groq website lookup error:`, error.message);
    return null;
  }
};

/**
 * Extract service name from email
 * FIXED: Only extract from subject/from, not body (body can have ads/links)
 * @param {string} subject - Email subject
 * @param {string} from - Email from address
 * @param {string} body - Email body
 * @returns {Object|null} Service info or null
 */
const extractServiceName = (subject, from, body) => {
  // PRIORITY 1: Check subject line first (most reliable)
  const subjectLower = subject.toLowerCase();
  for (const [pattern, serviceInfo] of Object.entries(SERVICE_PATTERNS)) {
    if (subjectLower.includes(pattern)) {
      console.log(`   🎯 Matched service "${serviceInfo.name}" from subject`);
      return serviceInfo;
    }
  }

  // PRIORITY 2: Check sender email domain
  const emailMatch = from.match(/@([^>]+)/);
  if (emailMatch) {
    const domain = emailMatch[1].toLowerCase();

    // Extract main domain (e.g., "paypal.com" from "news.paypal.com")
    const domainParts = domain.split('.');
    const mainDomain = domainParts.length >= 2
      ? domainParts.slice(-2, -1)[0]
      : domainParts[0];

    // Check if main domain matches a known service
    for (const [pattern, serviceInfo] of Object.entries(SERVICE_PATTERNS)) {
      if (mainDomain === pattern || domain.includes(pattern)) {
        console.log(`   🎯 Matched service "${serviceInfo.name}" from email domain`);
        return serviceInfo;
      }
    }

    // Only return domain-based name if it looks like a real company
    // Exclude generic domains like "news", "mail", "noreply", "info", etc.
    const genericDomains = ['news', 'mail', 'noreply', 'info', 'support', 'help', 'notifications', 'updates', 'alerts'];
    if (!genericDomains.includes(mainDomain) && mainDomain.length >= 4) {
      console.log(`   ℹ️  Extracted service "${mainDomain}" from email domain`);
      return {
        name: mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1),
        category: 'Software',
        logo: '📋'
      };
    }
  }

  console.log(`   ❌ Could not extract service name`);
  return null;
};

/**
 * Extract price from email text
 * @param {string} text - Email text
 * @returns {number|null} Price or null
 */
const extractPrice = (text) => {
  // Match common price patterns: $XX.XX, NZ$XX.XX, USD XX.XX, €XX.XX, etc.
  const pricePatterns = [
    /\$\s*(\d+(?:\.\d{2})?)/,  // $29.99
    /[A-Z]{2,3}\$\s*(\d+(?:\.\d{2})?)/,  // NZ$29.99, AU$29.99
    /(\d+(?:\.\d{2})?)\s*USD/i,  // 29.99 USD
    /(\d+(?:\.\d{2})?)\s*NZD/i,  // 29.99 NZD
    /(\d+(?:\.\d{2})?)\s*AUD/i,  // 29.99 AUD
    /(\d+(?:\.\d{2})?)\s*EUR/i,  // 29.99 EUR
    /(\d+(?:\.\d{2})?)\s*GBP/i,  // 29.99 GBP
    /total[:\s]+\$?\s*(\d+(?:\.\d{2})?)/i,
    /amount[:\s]+\$?\s*(\d+(?:\.\d{2})?)/i,
    /price[:\s]+\$?\s*(\d+(?:\.\d{2})?)/i,
    /monthly charge[:\s\w]*\$?\s*(\d+(?:\.\d{2})?)/i  // "Monthly charge ... $29.99"
  ];

  for (const pattern of pricePatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const price = parseFloat(match[1]);
      if (price > 0 && price < 10000) { // Sanity check
        return price;
      }
    }
  }

  return null;
};

/**
 * Extract billing cycle from email text with context-aware matching
 * Avoids matching cycle keywords in marketing text by looking for billing-related context
 * @param {string} text - Email text
 * @returns {string} Billing cycle
 */
const extractBillingCycle = (text) => {
  const lowerText = text.toLowerCase();

  // Priority 1: Look for explicit price patterns with cycle (most reliable)
  // e.g., "$9.99/month", "$99/year", "per month", "per year"
  if (/\$[\d.,]+\s*\/\s*year|per\s+year|annual(ly)?\s+(subscription|plan|billing|payment)/.test(lowerText)) {
    return 'yearly';
  } else if (/\$[\d.,]+\s*\/\s*month|per\s+month|month(ly)?\s+(subscription|plan|billing|payment)/.test(lowerText)) {
    return 'monthly';
  } else if (/\$[\d.,]+\s*\/\s*quarter|per\s+quarter|quarter(ly)?\s+(subscription|plan|billing|payment)/.test(lowerText)) {
    return 'quarterly';
  } else if (/\$[\d.,]+\s*\/\s*week|per\s+week|week(ly)?\s+(subscription|plan|billing|payment)/.test(lowerText)) {
    return 'weekly';
  }

  // Priority 2: Look for cycle keywords near billing-related terms
  // This avoids matching "year" in marketing text like "save all year"
  const billingContext = /(billing|payment|subscription|charged|renew|membership|plan)\s+(?:is\s+)?(annual|yearly|monthly|quarterly|weekly)/i;
  const reverseContext = /(annual|yearly|monthly|quarterly|weekly)\s+(billing|payment|subscription|plan|membership)/i;

  const match = lowerText.match(billingContext) || lowerText.match(reverseContext);
  if (match) {
    const cycle = match[1] === 'billing' || match[1] === 'payment' || match[1] === 'subscription' || match[1] === 'charged' || match[1] === 'renew' || match[1] === 'membership' || match[1] === 'plan' ? match[2] : match[1];
    if (cycle === 'annual' || cycle === 'yearly') return 'yearly';
    if (cycle === 'quarterly') return 'quarterly';
    if (cycle === 'weekly') return 'weekly';
    if (cycle === 'monthly') return 'monthly';
  }

  // Priority 3: Fallback to simple keyword matching (but stricter than before)
  // Only match if "annual" or "yearly" (not just "year")
  if (lowerText.includes('annual') || lowerText.includes('yearly')) {
    return 'yearly';
  } else if (lowerText.includes('quarterly') || lowerText.includes('quarter')) {
    return 'quarterly';
  } else if (lowerText.includes('weekly') || lowerText.includes('week')) {
    return 'weekly';
  }

  return 'monthly'; // Default to monthly
};

/**
 * Extract next billing date from email text
 * @param {string} text - Email text
 * @returns {Date|null} Next billing date or null
 */
const extractNextBillingDate = (text) => {
  // Match date patterns - broader to catch different formats
  const datePatterns = [
    // "FIRST RENEWAL DATE 25 September 2025"
    /(?:first renewal date|renewal date|next renewal)[:\s]*(\d{1,2} \w+ \d{4})/i,

    // "next billing: Jan 15, 2024"
    /(?:next billing|next payment|renew|renews on)[:\s]+(\w+ \d{1,2},? \d{4})/i,
    /(?:next billing|next payment|renew|renews on)[:\s]+(\d{1,2} \w+ \d{4})/i,

    // "Sep 22 – Oct 22, 2025" (end date from range)
    /–\s*(\w+ \d{1,2},? \d{4})/i,

    // ISO format "2024-01-15"
    /(?:next billing|renewal|next payment)[:\s]+(\d{4}-\d{2}-\d{2})/i,

    // "will be charged $23.00/month" - estimate next month
    /(?:charged|charge you)\s+\$[\d.]+\s*\/\s*month/i,
  ];

  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const date = new Date(match[1]);
      if (!isNaN(date.getTime()) && date.getFullYear() >= 2020 && date.getFullYear() <= 2030) {
        return date;
      }
    }
  }

  // If we find "/month" language, consider it valid even without explicit date
  if (/(?:charged|charge|bill|billed)\s+.*?\/\s*month/i.test(text)) {
    // Return next month as estimated billing date
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth;
  }

  return null;
};

/**
 * Extract card last 4 digits from email text
 * @param {string} text - Email text
 * @returns {string|null} Card last 4 or null
 */
const extractCardInfo = (text) => {
  // Match patterns like "visa-3400", "Visa - 3322", "VISA ending in 1431"
  const cardPatterns = [
    /(?:visa|mastercard|amex|discover)[\s-]+(\d{4})/i,
    /(?:card ending in|ending in)\s*(\d{4})/i,
    /payment method[:\s]+(?:visa|mastercard|amex|discover)[\s-]+(\d{4})/i,
    /\*{4}[\s-]*(\d{4})/,
    /•{4}[\s-]*(\d{4})/,
    /x{4}[\s-]*(\d{4})/i,
    /card\s*\([^)]*?-?\s*(\d{4})\)/i
  ];

  for (const pattern of cardPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      return `****${match[1]}`;
    }
  }

  return null;
};

/**
 * Validate if email is subscription-related (invoices, cancellations, trials, receipts)
 * RELAXED: Accept multiple types of subscription events
 * @param {string} subject - Email subject
 * @param {string} body - Email body
 * @returns {boolean} True if valid subscription email
 */
const isValidSubscriptionInvoice = (subject, body) => {
  const fullText = `${subject} ${body}`.toLowerCase();
  const subjectLower = subject.toLowerCase();

  console.log(`   📋 Checking validation...`);

  // 1. EXCLUDE obvious one-time purchases (very specific)
  const oneTimePurchaseIndicators = [
    'trip receipt',
    'ride receipt',
    'your trip with',
    'trip with uber',
    'thanks for riding',
    'shipment',
    'has shipped',
    'out for delivery',
    'tracking number',
    'track your package'
  ];

  const isOneTimePurchase = oneTimePurchaseIndicators.some(indicator =>
    fullText.includes(indicator)
  );

  if (isOneTimePurchase) {
    console.log(`   └─ ❌ One-time purchase (ride/delivery)`);
    return false;
  }

  // 2. ACCEPT cancellation emails (important subscription events!)
  const cancellationKeywords = [
    'subscription was canceled',
    'subscription has been canceled',
    'membership was canceled',
    'membership has been canceled',
    'subscription ended',
    'membership ended'
  ];

  if (cancellationKeywords.some(kw => fullText.includes(kw))) {
    console.log(`   └─ ✅ Cancellation email (accepted)`);
    return true;
  }

  // 3. ACCEPT receipts/invoices (they're always relevant)
  const receiptKeywords = ['receipt', 'invoice'];
  if (receiptKeywords.some(kw => subjectLower.includes(kw))) {
    console.log(`   └─ ✅ Receipt/Invoice in subject (accepted)`);
    return true;
  }

  // 4. ACCEPT trial starts/confirmations (important events)
  const trialStartKeywords = [
    'trial begins',
    'trial started',
    'free trial',
    'trial period'
  ];

  if (trialStartKeywords.some(kw => fullText.includes(kw))) {
    // Make sure it's not just a promotional "start your trial" email
    if (!fullText.includes('start your') && !fullText.includes('try for free')) {
      console.log(`   └─ ✅ Trial confirmation (accepted)`);
      return true;
    }
  }

  // 5. Check for subscription/membership keywords
  const subscriptionKeywords = [
    'subscription',
    'membership',
    'recurring',
    'auto-renew',
    'monthly charge',
    'billing starts'
  ];

  const hasSubscriptionKeyword = subscriptionKeywords.some(keyword =>
    fullText.includes(keyword)
  );

  if (hasSubscriptionKeyword) {
    console.log(`   └─ ✅ Has subscription keywords (accepted)`);
    return true;
  }

  // 6. If none of the above, reject
  console.log(`   └─ ❌ No subscription indicators found`);
  return false;
};

/**
 * Parse subscription information from email
 * @param {Object} message - Gmail message object
 * @returns {Object|null} Parsed subscription data or null
 */
const parseSubscriptionFromEmail = (message) => {
  try {
    const subject = getHeader(message, 'subject') || '';
    const from = getHeader(message, 'from') || '';
    const date = getHeader(message, 'date');
    const body = getEmailBody(message);

    const fullText = `${subject} ${body}`;

    // DEBUG: Log what we're processing
    const shortSubject = subject.substring(0, 80);
    const shortFrom = from.substring(0, 40);
    console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`📧 PROCESSING EMAIL`);
    console.log(`   Subject: "${shortSubject}${subject.length > 80 ? '...' : ''}"`);
    console.log(`   From: ${shortFrom}`);
    console.log(`   Date: ${date}`);

    // Extract ALL fields first
    const serviceInfo = extractServiceName(subject, from, body);
    const price = extractPrice(fullText);
    const nextBillingDate = extractNextBillingDate(fullText);
    const billingCycle = extractBillingCycle(fullText);
    const cardInfo = extractCardInfo(fullText);

    // DEBUG: Show what was extracted
    console.log(`\n🔍 EXTRACTION RESULTS:`);
    console.log(`   Service: ${serviceInfo?.name || '❌ NONE'} (${serviceInfo?.category || 'N/A'})`);
    console.log(`   Price: ${price ? '✅ $' + price : '❌ NONE'}`);
    console.log(`   Billing Cycle: ${billingCycle || 'N/A'}`);
    console.log(`   Next Billing: ${nextBillingDate ? '✅ ' + nextBillingDate.toLocaleDateString() : '❌ NONE'}`);
    console.log(`   Card: ${cardInfo || 'N/A'}`);

    // Now validate with all extracted data
    console.log(`\n🧪 VALIDATION CHECKS:`);

    const isValidInvoice = isValidSubscriptionInvoice(subject, body);
    console.log(`   1. Valid Invoice? ${isValidInvoice ? '✅ YES' : '❌ NO'}`);

    if (!isValidInvoice) {
      console.log(`   └─ REJECTED: Not a valid subscription invoice`);
      return null;
    }

    console.log(`   2. Has Service? ${serviceInfo ? '✅ YES' : '❌ NO'}`);
    if (!serviceInfo) {
      console.log(`   └─ REJECTED: Cannot identify service`);
      return null;
    }

    // Filter out garbage service names (too short, generic, etc.)
    const serviceName = serviceInfo.name;
    if (serviceName.length < 3) {
      console.log(`   └─ REJECTED: Service name too short: "${serviceName}"`);
      return null;
    }

    const genericNames = ['mail', 'email', 'e', 'eg', 'test', 'inc', 'llc', 'com'];
    if (genericNames.includes(serviceName.toLowerCase())) {
      console.log(`   └─ REJECTED: Generic service name: "${serviceName}"`);
      return null;
    }

    // Check if this is a cancellation or trial email (they won't have prices)
    const isCancellation = subject.toLowerCase().includes('canceled') ||
      subject.toLowerCase().includes('cancelled');
    const isTrial = fullText.toLowerCase().includes('trial') ||
      fullText.toLowerCase().includes('free trial');

    console.log(`   3. Has Price? ${price && price > 0 ? '✅ YES' : '❌ NO'}`);

    // Cancellations and trials don't need prices
    if (!isCancellation && !isTrial) {
      if (!price || price <= 0) {
        console.log(`   └─ REJECTED: No valid price found (not cancellation/trial)`);
        return null;
      }

      // Filter out unrealistic prices
      if (price < 0.99 || price > 9999) {
        console.log(`   └─ REJECTED: Unrealistic price: $${price}`);
        return null;
      }
    } else {
      console.log(`   └─ ⚠️  No price (but OK for cancellation/trial)`);
    }

    console.log(`\n✅ EMAIL ACCEPTED!`);
    if (isCancellation) {
      console.log(`   → 🚫 CANCELLATION: ${serviceInfo.name}`);
    } else if (isTrial) {
      console.log(`   → 🎁 TRIAL: ${serviceInfo.name}${price ? ` - $${price}` : ''}`);
    } else {
      console.log(`   → 💰 SUBSCRIPTION: ${serviceInfo.name} - $${price}/${billingCycle}`);
    }

    if (nextBillingDate) {
      console.log(`   → Next billing: ${nextBillingDate.toLocaleDateString()}`);
    }

    // Generate email link
    const emailLink = `https://mail.google.com/mail/u/0/#inbox/${message.id}`;

    // Build notes with all extracted info
    const noteParts = [`Imported from Gmail on ${new Date().toLocaleDateString()}`];
    if (cardInfo) {
      noteParts.push(`Card: ${cardInfo}`);
    }
    if (nextBillingDate) {
      noteParts.push(`Next billing: ${nextBillingDate.toLocaleDateString()}`);
    }

    // Determine status based on email type
    let status = 'active';
    let eventType = 'invoice';

    if (isCancellation) {
      status = 'cancelled';
      eventType = 'cancellation';
    } else if (isTrial) {
      status = 'active';
      eventType = 'trial_start';
    }

    return {
      name: serviceInfo.name,
      service: serviceInfo.name,
      category: serviceInfo.category,
      logo: serviceInfo.logo,
      price: price || 0, // Default to 0 for cancellations/trials
      currency: 'USD', // Default to USD for now
      billing_cycle: billingCycle,
      next_billing_date: nextBillingDate ? nextBillingDate.toISOString().split('T')[0] : null,
      subscription_date: date ? new Date(date).toISOString().split('T')[0] : null,
      status: status,
      event_type: eventType,
      email_link: emailLink,
      import_source: 'gmail',
      notes: noteParts.join(' | ')
    };
  } catch (error) {
    console.error('❌ Error parsing subscription from email:', error);
    return null;
  }
};

/**
 * Check if subscription already exists (duplicate detection)
 * @param {string} userId - User ID
 * @param {Object} subscription - Subscription data
 * @returns {Promise<boolean>} True if duplicate, false otherwise
 */
const isDuplicateSubscription = async (userId, subscription) => {
  try {
    // Check for exact match: same service name, price, and billing cycle
    const { data, error } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', userId)
      .eq('name', subscription.name)
      .eq('price', subscription.price)
      .eq('billing_cycle', subscription.billing_cycle)
      .limit(1);

    if (error) throw error;

    return data && data.length > 0;
  } catch (error) {
    console.error('Error checking duplicate subscription:', error);
    return false;
  }
};

/**
 * Scan Gmail account for subscriptions
 * @param {string} gmailAccountId - Gmail account ID
 * @returns {Promise<Object>} Scan results
 */
export const scanGmailForSubscriptions = async (gmailAccountId) => {
  // Declare variables at function scope so they're available in catch block
  let gmailAccount = null;
  let user = null;

  try {
    // Reset abort flag and create new AbortController
    _syncAborted = false;
    _syncAbortController = new AbortController();
    console.log('🔍 Starting Gmail scan for account:', gmailAccountId);

    // Get current user first (needed for error handling)
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    user = currentUser; // Set function-scope user variable

    // Load user's timezone settings and Tier-3 LLM preference
    // Force refresh to ensure we get the latest settings
    const userSettings = await getUserSettings(true);
    const userTimezone = userSettings?.timezone || 'America/New_York';

    // Check if user has access to Tier-3 LLM based on their plan
    const { getUserFeatures } = await import('./planService');
    const userFeatures = await getUserFeatures();
    const hasAccessToTier3 = userFeatures?.canUseAdvancedAIClassification || false;

    // Only use Tier-3 if user has access AND has it enabled in settings
    const useTier3LLM = hasAccessToTier3 && (userSettings?.tier3_llm_enabled || false);

    // Check if admin has enabled paid tier (admin-only setting stored in localStorage)
    const tierMode = getTierMode();
    const usePaidTier = tierMode === 'paid' && useTier3LLM;
    const paidModel = usePaidTier ? getSelectedPaidModel() : null;

    console.log(`⏰ User timezone: ${userTimezone}`);
    console.log(`🔑 Plan allows Tier-3 LLM: ${hasAccessToTier3 ? 'YES' : 'NO'}`);
    console.log(`⚙️  User enabled Tier-3 LLM: ${userSettings?.tier3_llm_enabled ? 'YES' : 'NO'}`);
    console.log(`🧠 Tier-3 LLM: ${useTier3LLM ? 'ENABLED ✅' : 'DISABLED ❌'}`);
    console.log(`💰 Admin Tier Mode: ${tierMode}`);
    console.log(`💰 Paid Tier: ${usePaidTier ? `ENABLED ✅ (Model: ${paidModel})` : 'DISABLED ❌'}`);

    // Update sync status to 'syncing'
    await supabase
      .from('synced_gmail_accounts')
      .update({
        sync_status: 'syncing',
        sync_error: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', gmailAccountId);

    // Get Gmail account details
    const { data: fetchedAccount, error: fetchError } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('id', gmailAccountId)
      .single();

    if (fetchError) throw fetchError;
    gmailAccount = fetchedAccount;

    // Detect if this is the first sync for this Gmail account
    // If last_sync_date is NULL, this is the first sync - skip savings recording for historical events
    const isFirstSync = !gmailAccount.last_sync_date;
    console.log(`🔍 First sync for ${gmailAccount.gmail_email}: ${isFirstSync ? 'YES - will skip savings recording for historical events' : 'NO - will record savings normally'}`);

    console.log(`🔑 Attempting to refresh access token for ${gmailAccount.gmail_email}...`);

    // Guard: Skip sync if no refresh token exists in the DB
    // This happens when addGmailAccountFromSession saved the account without a refresh token
    // (returning users via prompt:'select_account' don't get one from Google)
    if (!gmailAccount.gmail_refresh_token) {
      console.warn(`⚠️ No refresh token stored for ${gmailAccount.gmail_email} — skipping sync. User may need to re-authenticate.`);
      throw new Error(`No refresh token available for ${gmailAccount.gmail_email}. Please re-authenticate your Gmail account.`);
    }

    // Get access token using refresh token
    let accessToken;
    try {
      accessToken = await getGmailAccessToken(gmailAccount.gmail_refresh_token);
      console.log(`✅ Access token refreshed successfully`);
    } catch (tokenError) {
      console.error(`❌ Failed to refresh access token:`, tokenError);
      console.error(`   Error message: ${tokenError.message}`);

      // This will be caught by the outer catch block and handled properly
      throw new Error(`Token refresh failed: ${tokenError.message}`);
    }

    checkAbort();
    // Fetch subscription emails
    const messages = await fetchSubscriptionEmails(accessToken);
    checkAbort();
    console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`📧 STEP 1: Gmail fetch complete - ${messages.length} emails retrieved`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

    // Use LLM-based filtering instead of rule-based
    let parsedSubscriptions = [];
    let filteredOutCount = 0;
    let totalSaved = 0;
    let totalSkipped = 0;

    try {
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(`🤖 STEP 2: Preparing for classification...`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);

      // Get categories for immediate DB saves (user already fetched earlier)

      const { data: categories } = await supabase
        .from('categories')
        .select('id, name');

      const categoryMap = {};
      categories?.forEach(cat => {
        categoryMap[cat.name] = cat.id;
      });

      // Note: Deduplication by email ID now happens BEFORE classification (see Step 2.5)
      // This section is kept for reference but email_link dedup is no longer needed

      // Callback to save each batch immediately to database
      const saveBatchToDb = async (subscriptions) => {
        if (subscriptions.length === 0) return;

        // TWO-PASS PROCESSING: Sort by CHRONOLOGICAL ORDER first, then by event type
        // 1. Sort by date (oldest first) to detect price changes correctly
        // 2. Then sort invoices/renewals BEFORE cancellations within same service
        const sortedSubscriptions = subscriptions.sort((a, b) => {
          // First priority: Sort by email date (oldest first)
          const aDate = new Date(a.original_email_data?.date || 0);
          const bDate = new Date(b.original_email_data?.date || 0);

          // If dates are different, sort chronologically
          if (aDate.getTime() !== bDate.getTime()) {
            return aDate - bDate; // Oldest first
          }

          // If same date/time, then sort by event type (invoices before cancellations)
          const aIsCancellation = a.event_type === 'cancellation';
          const bIsCancellation = b.event_type === 'cancellation';
          if (aIsCancellation && !bIsCancellation) return 1;  // a after b
          if (!aIsCancellation && bIsCancellation) return -1; // a before b
          return 0; // keep original order
        });

        console.log(`\n   💾 Saving batch to database (${sortedSubscriptions.length} subscriptions)...`);
        console.log(`   📋 Processing order: CHRONOLOGICAL (oldest first) → Invoices before Cancellations`);

        // Log the actual processing order for debugging
        console.log(`   📅 Email dates in processing order:`);
        sortedSubscriptions.forEach((sub, idx) => {
          const emailDate = new Date(sub.original_email_data?.date);
          console.log(`      ${idx + 1}. ${emailDate.toLocaleDateString()} - ${sub.name} ($${sub.price}) - ${sub.event_type}`);
        });

        let batchSaved = 0;
        let batchSkipped = 0;
        let batchFailed = 0;

        for (const subscription of sortedSubscriptions) {
          checkAbort();
          try {
            // Subscription is already in the correct format from convertClassifierToSubscriptionFormat
            // Just need to add the imported_from_email field
            subscription.imported_from_email = gmailAccount.gmail_email;

            console.log(`      📝 Processing: ${subscription.name} ($${subscription.price}/${subscription.billing_cycle}) - Event: ${subscription.event_type}, Status: ${subscription.status}`);

            // Note: Email deduplication already happened in Step 2.5 (by email_id)
            // All emails reaching this point are guaranteed to be new

            // Get email date from original_email_data
            const emailDate = subscription.original_email_data?.date ? new Date(subscription.original_email_data.date) : new Date();
            console.log(`      📅 Email timestamp: ${subscription.original_email_data?.date} → ${emailDate.toISOString()}`);
            console.log(`      📅 Email local date: ${emailDate.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}`);
            console.log(`      📅 Classifier returned last_billing_date: ${subscription.last_billing_date || 'NULL'}`);

            // ========================================================================
            // MASTER SUBSCRIPTION + EVENTS ARCHITECTURE
            // ========================================================================
            // Strategy: Find master subscription by service name OR email domain
            // Then create event in subscription_events table
            // ========================================================================

            console.log(`\n      🔍 SEARCHING FOR MASTER SUBSCRIPTION:`);
            console.log(`         Service: "${subscription.service}"`);
            console.log(`         Name: "${subscription.name}"`);
            console.log(`         Event type: ${subscription.event_type}`);
            console.log(`         Email date: ${emailDate.toISOString()}`);
            console.log(`         Price: $${subscription.price}`);
            console.log(`         Email subject: "${subscription.original_email_data?.subject}"`);
            console.log(`         Email from: ${subscription.original_email_data?.from}`);

            // Try 1: Exact service name match
            let { data: existingSubscriptions } = await supabase
              .from('subscriptions')
              .select('*')
              .eq('user_id', user.id)
              .eq('imported_from_email', gmailAccount.gmail_email)
              .eq('service', subscription.service)
              .order('created_at', { ascending: false });

            // Try 2: If no exact match, try email domain matching
            // Extract domain from email sender
            // Handles formats: "Name <email@domain.com>", "<email@domain.com>", "email@domain.com"
            // EXCEPTION: Do NOT use domain matching for Apple emails - they all come from same domain
            // but represent different apps (Hinge, iCloud, iMic, etc.)
            const emailFrom = subscription.original_email_data?.from || '';
            const isAppleEmail = emailFrom.toLowerCase().includes('@email.apple.com') || emailFrom.toLowerCase().includes('@apple.com');

            if (!existingSubscriptions || existingSubscriptions.length === 0) {
              // Skip domain matching for Apple emails - rely only on service name matching
              if (isAppleEmail) {
                console.log(`      ⚠️  Apple email detected - skipping domain matching (all Apple emails share same domain)`);
                console.log(`         Service extracted: "${subscription.service}"`);
                console.log(`         Will create NEW subscription since no exact service match found`);
                existingSubscriptions = [];
              } else {
                // Extract email address from various formats, then get domain
                const emailMatch = emailFrom.match(/([a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,})/i);
                if (emailMatch) {
                  const emailAddr = emailMatch[1].toLowerCase();
                  const senderBaseDomain = extractBaseDomain(emailAddr);
                  console.log(`      🔄 No exact service match, trying domain-based matching...`);
                  console.log(`         From: ${emailFrom}`);
                  console.log(`         Base domain: ${senderBaseDomain}`);

                  // Get all subscriptions for this user (including manual ones without imported_from_email)
                  const { data: allUserSubs } = await supabase
                    .from('subscriptions')
                    .select('*')
                    .eq('user_id', user.id)
                    .order('created_at', { ascending: false });

                  console.log(`         📊 Total user subscriptions: ${allUserSubs?.length || 0}`);
                  if (allUserSubs && allUserSubs.length > 0) {
                    console.log(`         📋 Checking against existing subscriptions:`, allUserSubs.map(s => ({
                      name: s.name,
                      website_url: s.website_url,
                      url: s.url,
                      email_from: s.original_email_data?.from,
                      imported_from: s.imported_from_email || 'manual'
                    })));
                  }

                  // Filter by matching base domain in email data OR website_url OR url
                  existingSubscriptions = allUserSubs?.filter(sub => {
                    console.log(`         🔍 Checking subscription: ${sub.name}`);

                    // Check 1: Match against original email data
                    const subEmailFrom = sub.original_email_data?.from || '';
                    if (subEmailFrom) {
                      const subEmailMatch = subEmailFrom.match(/([a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,})/i);
                      if (subEmailMatch) {
                        const subEmailAddr = subEmailMatch[1].toLowerCase();
                        const subBaseDomain = extractBaseDomain(subEmailAddr);
                        console.log(`            - Email from: ${subEmailFrom} → domain: ${subBaseDomain}`);
                        if (subBaseDomain === senderBaseDomain) {
                          console.log(`         ✅ Match found via email domain: ${sub.name} (${subBaseDomain})`);
                          return true;
                        }
                      }
                    } else {
                      console.log(`            - No email from data`);
                    }

                    // Check 2: Match against website_url (from imported subscriptions)
                    if (sub.website_url) {
                      const subWebsiteDomain = extractBaseDomain(sub.website_url);
                      console.log(`            - website_url: ${sub.website_url} → domain: ${subWebsiteDomain}`);
                      if (subWebsiteDomain === senderBaseDomain) {
                        console.log(`         ✅ Match found via website_url: ${sub.name} (${subWebsiteDomain})`);
                        return true;
                      }
                    } else {
                      console.log(`            - No website_url`);
                    }

                    // Check 3: Match against url (from manual subscriptions)
                    if (sub.url) {
                      const subUrlDomain = extractBaseDomain(sub.url);
                      console.log(`            - url: ${sub.url} → domain: ${subUrlDomain}`);
                      if (subUrlDomain === senderBaseDomain) {
                        console.log(`         ✅ Match found via url field: ${sub.name} (${subUrlDomain})`);
                        return true;
                      }
                    } else {
                      console.log(`            - No url field`);
                    }

                    console.log(`            ❌ No match for ${sub.name}`);
                    return false;
                  }) || [];

                  if (existingSubscriptions.length > 0) {
                    console.log(`      ✅ Found ${existingSubscriptions.length} subscription(s) by base domain matching!`);
                  } else {
                    console.log(`      ❌ No subscriptions found with base domain: ${senderBaseDomain}`);
                  }
                }
              } // End of else block for non-Apple emails
            }

            if (existingSubscriptions && existingSubscriptions.length > 0) {
              console.log(`      📋 Found ${existingSubscriptions.length} master subscription(s):`);
              existingSubscriptions.forEach((sub, idx) => {
                console.log(`         ${idx + 1}. ${sub.name} (${sub.service}) - Status: ${sub.status}, Price: $${sub.price}, Created: ${new Date(sub.created_at).toISOString()}`);
              });
            }

            // Find the most recent master subscription (latest created)
            // IMPORTANT: If multiple subscriptions exist with same service name, try to match by price first
            // This handles cases like Hinge Daily ($14.99) vs Hinge Monthly ($199.99) - different tiers
            let masterSubscription = null;

            if (existingSubscriptions && existingSubscriptions.length > 0) {
              // If we have multiple matches, try to find one with matching price
              if (existingSubscriptions.length > 1 && subscription.price > 0) {
                console.log(`      🔍 Multiple subscriptions found - attempting price-based match...`);
                console.log(`         Looking for price: $${subscription.price}`);

                // Try exact price match first
                const priceMatch = existingSubscriptions.find(sub => {
                  const priceDiff = Math.abs(sub.price - subscription.price);
                  return priceDiff < 0.01; // Within 1 cent (floating point tolerance)
                });

                if (priceMatch) {
                  console.log(`      ✅ Found exact price match: ${priceMatch.name} ($${priceMatch.price})`);
                  masterSubscription = priceMatch;
                } else {
                  console.log(`      ⚠️  No exact price match found`);
                  console.log(`         Existing prices: ${existingSubscriptions.map(s => `$${s.price}`).join(', ')}`);
                  console.log(`         New email price: $${subscription.price}`);
                  console.log(`         This appears to be a DIFFERENT subscription tier - will create new subscription`);
                  // Don't set masterSubscription - will create new one below
                  masterSubscription = null;
                }
              } else {
                // Only one match or no price available - use it
                masterSubscription = existingSubscriptions[0];
              }
            }

            // Map category name to ID
            const categoryId = categoryMap[subscription.category] || categoryMap['Other'];

            if (masterSubscription) {
              // ========================================================================
              // MASTER SUBSCRIPTION EXISTS - Create event + update master
              // ========================================================================
              console.log(`      📊 Master subscription found: ${masterSubscription.name} (ID: ${masterSubscription.id})`);
              console.log(`      ➕ Creating subscription event...`);

              const eventPayload = {
                subscription_id: masterSubscription.id,
                event_type: subscription.event_type,
                event_date: emailDate.toISOString(),
                email_id: subscription.original_email_data?.id || null,
                email_subject: subscription.original_email_data?.subject || null,
                email_from: subscription.original_email_data?.from || null,
                email_link: subscription.email_link,
                price: subscription.price,
                currency: subscription.currency,
                original_email_data: subscription.original_email_data,
                classifier_confidence: subscription.original_email_data?.classifier_confidence || null,
                classifier_tier: subscription.original_email_data?.tier_used || null,
                classification_evidence: subscription.original_email_data?.evidence || null
              };

              console.log(`      📝 Event payload:`, {
                subscription_id: eventPayload.subscription_id,
                event_type: eventPayload.event_type,
                event_date: eventPayload.event_date,
                email_subject: eventPayload.email_subject,
                price: eventPayload.price,
                is_cancellation: subscription.event_type === 'cancellation',
                raw_date_header: subscription.original_email_data?.date
              });

              // Create event in subscription_events table
              const { data: eventData, error: eventError } = await supabase
                .from('subscription_events')
                .insert(eventPayload)
                .select();

              if (eventError) {
                console.error(`      ❌ Event Error for ${subscription.name}:`, eventError);
                console.error(`      ❌ Full error details:`, JSON.stringify(eventError, null, 2));
                batchFailed++;
              } else {
                console.log(`      ✅ Event created successfully!`);
                console.log(`      📊 Event ID: ${eventData?.[0]?.id}`);
                console.log(`      📊 Event type: ${subscription.event_type}`);

                // Update master subscription with latest info
                const updateData = {
                  updated_at: new Date().toISOString()
                };

                // Always update to latest email data and event info
                const masterCreated = new Date(masterSubscription.created_at);
                if (emailDate >= masterCreated) {
                  updateData.email_link = subscription.email_link;
                  updateData.event_type = subscription.event_type;
                  updateData.original_email_data = subscription.original_email_data;
                }

                // Update status if cancellation
                if (subscription.event_type === 'cancellation') {
                  console.log(`      🚫 CANCELLATION detected - updating subscription status to cancelled`);
                  updateData.status = 'cancelled';
                  updateData.cancellation_date = getDateInTimezone(emailDate, userTimezone);
                  console.log(`      📅 Cancellation date: ${updateData.cancellation_date}`);

                  // Record savings event if master subscription was active
                  // Skip on first sync to avoid recording historical savings
                  if (masterSubscription.status === 'active' && masterSubscription.price > 0) {
                    if (!isFirstSync) {
                      console.log(`      💰 Recording savings event for cancelled subscription...`);
                      try {
                        await recordSavingsEvent(
                          user.id,
                          masterSubscription.id,
                          masterSubscription.name,
                          'canceled', // Use 'canceled' not 'cancelled' for consistency with event type
                          parseFloat(masterSubscription.price),
                          0,
                          `Cancelled via Gmail sync - detected from email on ${updateData.cancellation_date}`
                        );
                        console.log(`      ✅ Savings event recorded: $${masterSubscription.price}/mo saved`);
                      } catch (savingsError) {
                        console.error(`      ⚠️  Failed to record savings event:`, savingsError.message);
                        // Don't throw - continue with subscription update
                      }
                    } else {
                      console.log(`      ⚠️  First sync - skipping savings recording for historical cancellation`);
                    }
                  }
                }

                // Update price and dates if this is an invoice/renewal with newer date
                if ((subscription.event_type === 'invoice' || subscription.event_type === 'renewal' || subscription.event_type === 'subscription_start' || subscription.event_type === 'subscription') && subscription.price) {
                  const masterLastBilling = masterSubscription.last_billing_date ? new Date(masterSubscription.last_billing_date) : null;

                  // Get billing date from classifier or fall back to email date in user's timezone
                  let newLastBillingDate = subscription.last_billing_date || getDateInTimezone(emailDate, userTimezone);
                  const newLastBillingDateObj = new Date(newLastBillingDate);

                  console.log(`      💳 BILLING DATE CHECK:`);
                  console.log(`         Master last billing: ${masterLastBilling ? masterLastBilling.toISOString() : 'NULL'}`);
                  console.log(`         New email billing: ${newLastBillingDateObj.toISOString()}`);
                  console.log(`         Is newer? ${!masterLastBilling || newLastBillingDateObj > masterLastBilling}`);

                  // Update last_billing_date if this is a newer billing
                  if (!masterLastBilling || newLastBillingDateObj > masterLastBilling) {
                    // ========================================================================
                    // PRICE CHANGE DETECTION: Track price changes in price_events table
                    // ========================================================================
                    const oldPrice = parseFloat(masterSubscription.price);
                    const newPrice = parseFloat(subscription.price);

                    console.log(`      💵 PRICE COMPARISON:`);
                    console.log(`         Master price: $${oldPrice} (type: ${typeof masterSubscription.price})`);
                    console.log(`         New email price: $${newPrice} (type: ${typeof subscription.price})`);
                    console.log(`         oldPrice valid? ${!isNaN(oldPrice)}`);
                    console.log(`         newPrice valid? ${!isNaN(newPrice)}`);
                    console.log(`         Prices different? ${oldPrice !== newPrice}`);

                    if (!isNaN(oldPrice) && !isNaN(newPrice) && oldPrice !== newPrice) {
                      const changeAmount = newPrice - oldPrice;
                      const changePercentage = parseFloat(((changeAmount / oldPrice) * 100).toFixed(2));

                      // Classify the price change based on threshold
                      // Changes > 15% are likely plan changes (upgrades/downgrades)
                      // Changes <= 15% are likely company-imposed price increases
                      const THRESHOLD_PERCENTAGE = 15;
                      const eventType = Math.abs(changePercentage) > THRESHOLD_PERCENTAGE
                        ? 'plan_change'
                        : 'price_increase';

                      console.log(`      💰 PRICE CHANGE DETECTED!`);
                      console.log(`         Old: $${oldPrice} → New: $${newPrice}`);
                      console.log(`         Change: $${changeAmount.toFixed(2)} (${changePercentage}%)`);
                      console.log(`         Classification: ${eventType} (threshold: ${THRESHOLD_PERCENTAGE}%)`);

                      try {
                        // Create price_events record
                        const { data: priceEventData, error: priceEventError } = await supabase
                          .from('price_events')
                          .insert({
                            subscription_id: masterSubscription.id,
                            old_price: oldPrice,
                            new_price: newPrice,
                            change_date: newLastBillingDate,
                            change_percentage: changePercentage,
                            event_type: eventType,
                            user_override: false,
                            notes: `Auto-detected from ${subscription.event_type} email: "${subscription.original_email_data?.subject || 'N/A'}"`
                          })
                          .select();

                        if (priceEventError) {
                          console.error(`      ❌ Failed to create price event:`, priceEventError);
                          console.error(`      ❌ Error code:`, priceEventError.code);
                          console.error(`      ❌ Error message:`, priceEventError.message);
                          console.error(`      ❌ Full error:`, JSON.stringify(priceEventError, null, 2));
                        } else {
                          console.log(`      ✅ PRICE CHANGE LOGGED TO price_events TABLE!`);
                          console.log(`         Event ID: ${priceEventData?.[0]?.id}`);
                          console.log(`         Subscription ID: ${masterSubscription.id}`);
                          console.log(`         Change: $${oldPrice} → $${newPrice} (+${changePercentage}%)`);
                          console.log(`         Change date: ${newLastBillingDate}`);

                          // Record savings event if price decreased (downgrade)
                          // Skip on first sync to avoid recording historical savings
                          if (changeAmount < 0) {
                            if (!isFirstSync) {
                              console.log(`      💰 Recording savings event for price decrease (downgrade)...`);
                              try {
                                await recordSavingsEvent(
                                  user.id,
                                  masterSubscription.id,
                                  masterSubscription.name,
                                  'downgraded',
                                  oldPrice,
                                  newPrice,
                                  `Downgraded via Gmail sync - detected from email on ${newLastBillingDate}`
                                );
                                console.log(`      ✅ Savings event recorded: $${Math.abs(changeAmount).toFixed(2)}/mo saved from downgrade`);
                              } catch (savingsError) {
                                console.error(`      ⚠️  Failed to record savings event:`, savingsError.message);
                                // Don't throw - continue with notification
                              }
                            } else {
                              console.log(`      ⚠️  First sync - skipping savings recording for historical downgrade`);
                            }
                          }

                          // Create notification for price change
                          // Only notify for actual price increases, not plan changes
                          try {
                            const serviceName = masterSubscription.service || masterSubscription.name;
                            if (changeAmount > 0 && eventType === 'price_increase') {
                              // Price increase (company-imposed)
                              await notifyPriceIncrease(
                                user.id,
                                serviceName,
                                oldPrice,
                                newPrice,
                                changePercentage,
                                masterSubscription.id
                              );
                              console.log(`      🔔 Price increase notification created`);
                            } else if (changeAmount > 0 && eventType === 'plan_change') {
                              // Plan upgrade - no notification needed
                              console.log(`      ℹ️  Plan upgrade detected - skipping price increase notification`);
                            } else if (changeAmount < 0) {
                              // Price decrease
                              await notifyPriceDecrease(
                                user.id,
                                serviceName,
                                oldPrice,
                                newPrice,
                                changePercentage,
                                masterSubscription.id
                              );
                              console.log(`      🔔 Price decrease notification created`);
                            }
                          } catch (notifError) {
                            console.error(`      ⚠️  Error creating price change notification:`, notifError);
                          }
                        }
                      } catch (priceEventErr) {
                        console.error(`      ⚠️  Error logging price change:`, priceEventErr);
                      }
                    }

                    // Check for billing cycle change (monthly → yearly = annual switch savings)
                    const oldCycle = masterSubscription.billing_cycle || masterSubscription.billingCycle;
                    const newCycle = subscription.billing_cycle;

                    if (oldCycle && newCycle && oldCycle !== newCycle) {
                      console.log(`      🔄 BILLING CYCLE CHANGE DETECTED!`);
                      console.log(`         Old cycle: ${oldCycle}`);
                      console.log(`         New cycle: ${newCycle}`);

                      // Check if this is a switch to annual billing from monthly
                      const wasMonthly = oldCycle === 'monthly';
                      const nowAnnual = newCycle === 'yearly' || newCycle === 'annual';

                      if (wasMonthly && nowAnnual) {
                        const monthlyPrice = parseFloat(masterSubscription.price);
                        const annualPrice = parseFloat(subscription.price);
                        const oldAnnualCost = monthlyPrice * 12;

                        console.log(`      💰 Annual billing switch detected:`);
                        console.log(`         Monthly: $${monthlyPrice}/mo → Would cost $${oldAnnualCost}/year`);
                        console.log(`         Annual: $${annualPrice}/year`);
                        console.log(`         Savings: $${(oldAnnualCost - annualPrice).toFixed(2)}/year = $${((oldAnnualCost - annualPrice) / 12).toFixed(2)}/mo`);

                        if (monthlyPrice > 0 && annualPrice > 0 && annualPrice < oldAnnualCost) {
                          // Skip on first sync to avoid recording historical savings
                          if (!isFirstSync) {
                            console.log(`      💰 Recording savings event for annual billing switch...`);
                            try {
                              await recordSavingsEvent(
                                user.id,
                                masterSubscription.id,
                                masterSubscription.name,
                                'annual_switch',
                                oldAnnualCost,
                                annualPrice,
                                `Switched to annual billing via Gmail sync - detected from email on ${newLastBillingDate}`
                              );
                              console.log(`      ✅ Savings event recorded: $${((oldAnnualCost - annualPrice) / 12).toFixed(2)}/mo saved (12-month benefit)`);
                            } catch (savingsError) {
                              console.error(`      ⚠️  Failed to record savings event:`, savingsError.message);
                              // Don't throw - continue with update
                            }
                          } else {
                            console.log(`      ⚠️  First sync - skipping savings recording for historical annual billing switch`);
                          }
                        }
                      }

                      // Update billing cycle
                      updateData.billing_cycle = newCycle;
                    }

                    // Update master subscription price
                    updateData.price = subscription.price;
                    updateData.currency = subscription.currency;
                    updateData.last_billing_date = newLastBillingDate;

                    // Use classifier's next_billing_date if available, otherwise calculate
                    if (subscription.next_billing_date) {
                      updateData.next_billing_date = subscription.next_billing_date;
                      console.log(`      📅 Using classifier next_billing_date: ${subscription.next_billing_date}`);
                    } else if (subscription.billing_cycle) {
                      // Calculate next billing date
                      // Parse date as local date (not UTC) to avoid timezone shift bugs
                      const [year, month, day] = newLastBillingDate.split('-').map(Number);
                      const lastDate = new Date(year, month - 1, day);
                      const nextDate = new Date(lastDate);

                      switch (subscription.billing_cycle) {
                        case 'weekly':
                          nextDate.setDate(nextDate.getDate() + 7);
                          break;
                        case 'monthly':
                          nextDate.setMonth(nextDate.getMonth() + 1);
                          break;
                        case 'quarterly':
                          nextDate.setMonth(nextDate.getMonth() + 3);
                          break;
                        case 'yearly':
                          nextDate.setFullYear(nextDate.getFullYear() + 1);
                          break;
                        default:
                          nextDate.setMonth(nextDate.getMonth() + 1);
                      }

                      updateData.next_billing_date = nextDate.toISOString().split('T')[0];
                      console.log(`      📅 Calculated next_billing_date: ${updateData.next_billing_date}`);
                    }

                    console.log(`      📅 Setting last_billing_date: ${updateData.last_billing_date} (from ${subscription.last_billing_date ? 'classifier' : 'email date'})`);
                  }
                }

                // 3-TIER URL EXTRACTION: Populate website_url if missing
                if (!masterSubscription.website_url || masterSubscription.website_url === '') {
                  console.log(`      🔗 Website URL missing, attempting 3-tier extraction...`);

                  let websiteUrl = null;

                  // Tier 1: Check if classifier already extracted it
                  if (subscription.website_url) {
                    websiteUrl = subscription.website_url;
                    console.log(`      ✅ Tier 1 (Classifier): ${websiteUrl}`);
                  }

                  // Tier 2: Extract from email body HTML
                  if (!websiteUrl && subscription.original_email_data?.body) {
                    websiteUrl = extractUrlFromEmailBody(
                      subscription.original_email_data.body,
                      subscription.service || subscription.name
                    );
                    if (websiteUrl) {
                      console.log(`      ✅ Tier 2 (Email Body): ${websiteUrl}`);
                    }
                  }

                  // Tier 3: Use Groq LLM fallback
                  if (!websiteUrl) {
                    websiteUrl = await getWebsiteUrlFromGroq(subscription.service || subscription.name);
                    if (websiteUrl) {
                      console.log(`      ✅ Tier 3 (Groq LLM): ${websiteUrl}`);
                    }
                  }

                  if (websiteUrl) {
                    updateData.website_url = websiteUrl;
                  } else {
                    console.log(`      ⚠️  Could not extract website URL from any tier`);
                  }
                }

                const { error: updateError } = await supabase
                  .from('subscriptions')
                  .update(updateData)
                  .eq('id', masterSubscription.id);

                if (updateError) {
                  console.error(`      ⚠️  Master update warning:`, updateError.message);
                } else {
                  console.log(`      ✅ Master subscription updated`);
                }

                totalSaved++;
                batchSaved++;
              }
            } else {
              // ========================================================================
              // NO MASTER SUBSCRIPTION - Create master + first event
              // ========================================================================
              console.log(`      ➕ Creating new master subscription + first event...`);

              // SIMPLIFIED URL EXTRACTION: Always use Groq LLM (most reliable)
              console.log(`      🔗 Using Groq LLM to find website URL...`);
              const websiteUrl = await getWebsiteUrlFromGroq(subscription.service || subscription.name);

              if (websiteUrl) {
                console.log(`      ✅ Groq LLM found: ${websiteUrl}`);
              } else {
                console.log(`      ⚠️  Could not find website URL via Groq`);
              }

              // Get billing dates - ALWAYS use email sent date (matches "Previous Emails" dropdown)
              // Convert email date to user's timezone
              const emailDateInUserTZ = getDateInTimezone(emailDate, userTimezone);
              console.log(`      📅 DEBUG: emailDate ISO string: ${emailDate.toISOString()}`);
              console.log(`      📅 DEBUG: emailDate in user timezone (${userTimezone}): ${emailDateInUserTZ}`);
              console.log(`      📅 DEBUG: Classifier extracted date (ignored): ${subscription.last_billing_date || 'NULL'}`);

              let lastBillingDate = null;
              if (subscription.event_type === 'invoice' || subscription.event_type === 'renewal' || subscription.event_type === 'subscription_start' || subscription.event_type === 'subscription') {
                // Use email sent date for billing date (same as "Previous Emails" dropdown)
                lastBillingDate = emailDateInUserTZ;
                console.log(`      📅 Using email sent date as last billing date: ${lastBillingDate}`);
              }

              let nextBillingDate = subscription.next_billing_date; // From classifier
              if (nextBillingDate) {
                console.log(`      📅 Using classifier-extracted next billing date: ${nextBillingDate}`);
              } else if (lastBillingDate && subscription.billing_cycle) {
                // Calculate if classifier didn't provide it
                console.log(`      📅 Calculating next billing date (classifier didn't provide it)`);

                // Parse date as local date (not UTC) to avoid timezone shift bugs
                const [year, month, day] = lastBillingDate.split('-').map(Number);
                const lastDate = new Date(year, month - 1, day);
                const nextDate = new Date(lastDate);

                switch (subscription.billing_cycle) {
                  case 'weekly':
                    nextDate.setDate(nextDate.getDate() + 7);
                    break;
                  case 'monthly':
                    nextDate.setMonth(nextDate.getMonth() + 1);
                    break;
                  case 'quarterly':
                    nextDate.setMonth(nextDate.getMonth() + 3);
                    break;
                  case 'yearly':
                    nextDate.setFullYear(nextDate.getFullYear() + 1);
                    break;
                  default:
                    nextDate.setMonth(nextDate.getMonth() + 1);
                }

                nextBillingDate = nextDate.toISOString().split('T')[0];
                console.log(`      📅 Calculated next billing date: ${nextBillingDate} (from last: ${lastBillingDate}, cycle: ${subscription.billing_cycle})`);
              }

              // Insert master subscription
              const { data: masterData, error: insertError } = await supabase
                .from('subscriptions')
                .insert({
                  user_id: user.id,
                  category_id: categoryId,
                  name: subscription.name,
                  service: subscription.service,
                  logo: subscription.logo,
                  price: subscription.price ?? 0,
                  currency: subscription.currency,
                  billing_cycle: subscription.billing_cycle,
                  last_billing_date: lastBillingDate,
                  next_billing_date: nextBillingDate,
                  subscription_date: subscription.subscription_date,
                  status: subscription.status,
                  notes: subscription.notes,
                  imported_from_email: subscription.imported_from_email,
                  email_link: subscription.email_link,
                  import_source: subscription.import_source,
                  event_type: subscription.event_type,
                  original_email_data: subscription.original_email_data,
                  website_url: websiteUrl
                })
                .select();

              if (!insertError && (subscription.event_type === 'invoice' || subscription.event_type === 'renewal' || subscription.event_type === 'subscription_start' || subscription.event_type === 'subscription')) {
                console.log(`      📅 Saved to DB - last_billing_date: ${lastBillingDate}`);
                console.log(`      📅 Saved to DB - next_billing_date: ${nextBillingDate}`);
                console.log(`      📅 Saved to DB - DB response:`, masterData?.[0]);
              }

              if (insertError) {
                console.error(`      ❌ DB Error for ${subscription.name}:`, insertError.message);
                console.error(`      ❌ Error details:`, JSON.stringify(insertError, null, 2));
                batchFailed++;
              } else {
                console.log(`      ✅ Master subscription created: ${subscription.name}`);
                console.log(`      📊 Master ID: ${masterData?.[0]?.id}`);

                // Create first event
                const newMasterId = masterData?.[0]?.id;
                if (newMasterId) {
                  console.log(`      ➕ Creating first event for new master subscription...`);

                  const firstEventPayload = {
                    subscription_id: newMasterId,
                    event_type: subscription.event_type,
                    event_date: emailDate.toISOString(),
                    email_id: subscription.original_email_data?.id || null,
                    email_subject: subscription.original_email_data?.subject || null,
                    email_from: subscription.original_email_data?.from || null,
                    email_link: subscription.email_link,
                    price: subscription.price,
                    currency: subscription.currency,
                    original_email_data: subscription.original_email_data,
                    classifier_confidence: subscription.original_email_data?.classifier_confidence || null,
                    classifier_tier: subscription.original_email_data?.tier_used || null,
                    classification_evidence: subscription.original_email_data?.evidence || null
                  };

                  console.log(`      📝 First event payload:`, {
                    subscription_id: firstEventPayload.subscription_id,
                    event_type: firstEventPayload.event_type,
                    event_date: firstEventPayload.event_date,
                    email_subject: firstEventPayload.email_subject,
                    price: firstEventPayload.price
                  });

                  const { data: firstEventData, error: eventError } = await supabase
                    .from('subscription_events')
                    .insert(firstEventPayload)
                    .select();

                  if (eventError) {
                    console.error(`      ⚠️  Event creation error:`, eventError);
                    console.error(`      ⚠️  Full error details:`, JSON.stringify(eventError, null, 2));
                  } else {
                    console.log(`      ✅ First event created successfully!`);
                    console.log(`      📊 Event ID: ${firstEventData?.[0]?.id}`);
                    console.log(`      📊 Event type: ${subscription.event_type}`);
                  }
                } else {
                  console.error(`      ❌ No master ID returned from insert!`);
                }

                totalSaved++;
                batchSaved++;
              }
            }
          } catch (conversionError) {
            console.error(`      ⚠️  Error processing subscription:`, conversionError.message);
            console.error(`      ⚠️  Stack:`, conversionError.stack);
            batchFailed++;
          }
        }

        console.log(`      ✅ Batch complete: Saved: ${batchSaved}, Skipped: ${batchSkipped}, Failed: ${batchFailed}`);

        // Query the actual total count from database for this Gmail account
        const { count: currentTotalCount } = await supabase
          .from('subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('imported_from_email', gmailAccount.gmail_email);

        // Emit progress event for UI to update subscription count in real-time
        window.dispatchEvent(new CustomEvent('gmail-sync-progress', {
          detail: {
            gmailAccountId,
            subscriptionsCount: currentTotalCount || 0,
            totalEmails: messages.length
          }
        }));
      };

      // Log emails after Gmail pull and before LLM processing
      console.log(`\n📧 EMAILS AFTER GMAIL PULL (before classifier):`);
      console.log(`   Total messages: ${messages.length}`);
      messages.forEach((msg, idx) => {
        console.log(`\n   Email ${idx + 1}/${messages.length}:`);
        console.log(`   - ID: ${msg.id}`);
        console.log(`   - Subject: ${msg.payload.headers.find(h => h.name === 'Subject')?.value || 'N/A'}`);
        console.log(`   - From: ${msg.payload.headers.find(h => h.name === 'From')?.value || 'N/A'}`);
        console.log(`   - Date: ${msg.payload.headers.find(h => h.name === 'Date')?.value || 'N/A'}`);
      });
      console.log(`\n`);

      // Transform Gmail API messages to classifier format
      console.log(`🔄 Transforming ${messages.length} Gmail messages to classifier format...`);
      const transformedEmails = messages.map(msg => {
        const headers = msg.payload.headers || [];
        const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';

        return {
          id: msg.id,
          threadId: msg.threadId,
          subject: getHeader('Subject'),
          from: getHeader('From'),
          to: getHeader('To'),
          date: getHeader('Date'),
          body: getEmailBody(msg), // Plain text for classification
          htmlBody: getEmailHtmlBody(msg), // HTML for rendering
          snippet: msg.snippet || '',
          headers: headers.reduce((acc, h) => ({ ...acc, [h.name]: h.value }), {}),
          labelIds: msg.labelIds || []
        };
      });
      console.log(`✅ Transformed ${transformedEmails.length} emails\n`);

      // Log transformed emails with bodies
      console.log(`📧 TRANSFORMED EMAILS (with bodies extracted):`);
      transformedEmails.slice(0, 5).forEach((email, idx) => {
        console.log(`\n   Email ${idx + 1}/${transformedEmails.length}:`);
        console.log(`   - Subject: ${email.subject}`);
        console.log(`   - From: ${email.from}`);
        console.log(`   - Body (first 200 chars): ${email.body.substring(0, 200)}...`);
      });
      console.log(`\n`);

      // ========================================================================
      // DEDUPLICATION: Filter out already-processed emails by Gmail message ID
      // ========================================================================
      console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(`🔍 STEP 2.5: Checking for already-processed emails...`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

      // Get all email IDs that this user has already processed
      let existingEmailIds = new Set();
      const { data: userSubscriptions } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', user.id);

      if (userSubscriptions && userSubscriptions.length > 0) {
        const subscriptionIds = userSubscriptions.map(s => s.id);

        // Get all email_ids from events for these subscriptions
        const { data: events } = await supabase
          .from('subscription_events')
          .select('email_id')
          .in('subscription_id', subscriptionIds)
          .not('email_id', 'is', null);

        if (events && events.length > 0) {
          existingEmailIds = new Set(events.map(e => e.email_id).filter(Boolean));
          console.log(`✅ Found ${existingEmailIds.size} already-processed email IDs (subscriptions)`);
        } else {
          console.log(`ℹ️  No existing subscription email IDs found (first sync?)`);
        }
      }

      // Also load dismissed email IDs (previously classified as not-a-subscription)
      const { data: dismissedRows } = await supabase
        .from('dismissed_email_ids')
        .select('email_id')
        .eq('gmail_account_id', gmailAccountId);

      let dismissedCount = 0;
      if (dismissedRows && dismissedRows.length > 0) {
        for (const row of dismissedRows) {
          existingEmailIds.add(row.email_id);
        }
        dismissedCount = dismissedRows.length;
        console.log(`✅ Found ${dismissedCount} previously dismissed email IDs (not subscriptions)`);
      }

      // Filter out emails that have already been processed
      const emailsBeforeDedup = transformedEmails.length;
      const newEmails = transformedEmails.filter(email => !existingEmailIds.has(email.id));
      const skippedDuplicates = emailsBeforeDedup - newEmails.length;

      console.log(`\n📊 Deduplication Results:`);
      console.log(`   Total emails fetched: ${emailsBeforeDedup}`);
      console.log(`   Skipped (known subscriptions): ${existingEmailIds.size - dismissedCount}`);
      console.log(`   Skipped (previously dismissed): ${dismissedCount}`);
      console.log(`   New emails to classify: ${newEmails.length}`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

      if (skippedDuplicates > 0) {
        console.log(`⏭️  Skipped ${skippedDuplicates} duplicate emails (already in database)`);
        console.log(`✅ This saves processing time by not re-classifying known emails\n`);
      }

      // Classify only NEW emails
      // If paid tier is enabled, use LLM-only batch classification
      // Otherwise, use hybrid classifier (Rules → ML → LLM)
      let classifierResults;
      if (usePaidTier) {
        console.log(`\n💰 PAID TIER: Using LLM-only batch classification`);
        console.log(`   Model: ${paidModel}`);
        classifierResults = await classifyEmailsPaidTier(newEmails, saveBatchToDb, paidModel, _syncAbortController?.signal);
      } else {
        console.log(`\n🆓 FREE TIER: Using hybrid classifier (Rules → ML → LLM)`);
        classifierResults = await classifyEmailsWithService(newEmails, saveBatchToDb, useTier3LLM, _syncAbortController?.signal);
      }

      console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(`📊 STEP 3: ${usePaidTier ? 'Paid Tier LLM' : 'Hybrid'} Classification & Database Save Complete`);
      console.log(`   ✅ Accepted as subscriptions: ${classifierResults.accepted.length}`);
      console.log(`   ❌ Rejected (not subscriptions): ${classifierResults.rejected.length}`);
      console.log(`   💾 Saved to database: ${totalSaved}`);
      console.log(`   ⏭️  Skipped (duplicates): ${totalSkipped}`);
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

      filteredOutCount = classifierResults.rejected.length;
      parsedSubscriptions = classifierResults.accepted; // Keep for compatibility with return value

      // Save rejected email IDs to dismissed_email_ids so they aren't re-classified next sync
      if (classifierResults.rejected.length > 0) {
        const rejectedIds = classifierResults.rejected
          .map(email => email.id)
          .filter(Boolean);

        if (rejectedIds.length > 0) {
          const dismissedRows = rejectedIds.map(emailId => ({
            user_id: user.id,
            gmail_account_id: gmailAccountId,
            email_id: emailId
          }));

          const { error: dismissError } = await supabase
            .from('dismissed_email_ids')
            .upsert(dismissedRows, { onConflict: 'gmail_account_id,email_id', ignoreDuplicates: true });

          if (dismissError) {
            console.warn('⚠️  Failed to save dismissed email IDs:', dismissError.message);
          } else {
            console.log(`💾 Saved ${rejectedIds.length} dismissed email IDs (won't re-classify next sync)`);
          }
        }
      }

    } catch (error) {
      console.error(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.error('❌ LLM FILTERING FAILED - NO FALLBACK');
      console.error(`Error: ${error.message}`);
      console.error(`Stack: ${error.stack}`);
      console.error(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.error(`\n⚠️  To fix this issue:`);
      console.error(`   1. Check your Groq API key in .env`);
      console.error(`   2. Ensure REACT_APP_USE_DIRECT_GROQ=true is set`);
      console.error(`   3. Check network connectivity`);
      console.error(`   4. See console for detailed error above\n`);

      // Re-throw the error - no fallback!
      throw error;
    }

    console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`📊 STEP 4: Sync Complete - Final Summary`);
    console.log(`   ✅ Total subscriptions found: ${parsedSubscriptions.length}`);
    console.log(`   🚫 Non-subscription emails filtered: ${filteredOutCount}`);
    console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

    // Database save already happened in batch callback during LLM filtering
    // Just use the counts we tracked
    const addedCount = totalSaved;
    const skippedCount = totalSkipped;

    // Get total count of subscriptions imported from this Gmail account
    const { count: totalCount } = await supabase
      .from('subscriptions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('imported_from_email', gmailAccount.gmail_email);

    // Update sync status to 'completed'
    await supabase
      .from('synced_gmail_accounts')
      .update({
        sync_status: 'completed',
        last_sync_date: new Date().toISOString(),
        subscriptions_count: totalCount || 0,
        updated_at: new Date().toISOString()
      })
      .eq('id', gmailAccountId);

    console.log(`✅ Gmail scan completed!`);
    console.log(`📊 Summary:`);
    console.log(`   📧 ${messages.length} emails scanned`);
    console.log(`   ✅ ${parsedSubscriptions.length} valid subscription invoices found`);
    console.log(`   🚫 ${filteredOutCount} emails filtered out (promotions/no price/unknown)`);
    console.log(`   ➕ ${addedCount} new subscriptions added`);
    console.log(`   ⏭️  ${skippedCount} duplicates skipped`);
    console.log(`   📊 Total subscriptions for this Gmail: ${totalCount || 0}`);

    // Create notification for successful sync
    if (addedCount > 0) {
      try {
        await notifySyncCompleted(user.id, gmailAccount.gmail_email, addedCount);
        console.log(`🔔 Sync notification created: ${addedCount} new subscriptions from ${gmailAccount.gmail_email}`);
      } catch (notifError) {
        console.error('⚠️  Error creating sync notification:', notifError);
      }
    }

    return {
      success: true,
      totalEmails: messages.length,
      parsedSubscriptions: parsedSubscriptions.length,
      addedCount,
      skippedCount
    };
  } catch (error) {
    console.error('Error scanning Gmail for subscriptions:', error);

    // Check if this is a token refresh error
    const isTokenError = error.message?.includes('Token refresh failed') ||
      error.message?.includes('Bad Request') ||
      error.message?.includes('invalid_grant') ||
      error.message?.includes('No refresh token');

    // Update sync status to 'failed' or 'auth_required'
    await supabase
      .from('synced_gmail_accounts')
      .update({
        sync_status: isTokenError ? 'auth_required' : 'failed',
        sync_error: error.message,
        updated_at: new Date().toISOString()
      })
      .eq('id', gmailAccountId);

    // Create notification for failed sync (only if we have user and gmailAccount)
    if (user && gmailAccount) {
      try {
        const notificationMessage = isTokenError
          ? `Gmail sync failed for ${gmailAccount.gmail_email}. Please re-authenticate your account.`
          : `Gmail sync failed for ${gmailAccount.gmail_email}: ${error.message}`;

        await notifySyncFailed(user.id, gmailAccount.gmail_email, notificationMessage);
        console.log(`🔔 Sync failure notification created for ${gmailAccount.gmail_email}`);
      } catch (notifError) {
        console.error('⚠️  Error creating sync failure notification:', notifError);
      }
    } else {
      console.log('⚠️  Cannot create sync failure notification: user or gmailAccount not available');
    }

    throw error;
  }
};

export default {
  scanGmailForSubscriptions,
  abortSync
};
